import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SaveIcon from '@mui/icons-material/Save'
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'

import { platformDistancePricing, savePlatformDistancePricing } from '../../apollo'

const GET_PRICING = gql`${platformDistancePricing}`
const SAVE_PRICING = gql`${savePlatformDistancePricing}`

/**
 * PlatformDistanceFeeEditor — the GLOBAL driving-distance fee schedule charged on
 * every platform order. The fee is the first tier whose "up to" distance covers
 * the customer's actual road distance from the restaurant; the farthest tier
 * applies at the edge of the restaurant's delivery boundary. Availability itself
 * is decided by the boundary (set per restaurant in the Boundaries tab).
 */
export default function PlatformDistancePricingEditor() {
  const [bands, setBands] = useState([])
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const { loading } = useQuery(GET_PRICING, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const p = data?.platformDistancePricing
      setBands((p?.bands || []).map(b => ({ maxMiles: String(b.maxMiles), fee: String(b.fee) })))
    },
  })

  const [mutSave, { loading: saving }] = useMutation(SAVE_PRICING, {
    onCompleted: () => {
      setSuccessMsg('Delivery fee tiers saved.')
      setErrorMsg('')
      setTimeout(() => setSuccessMsg(''), 4000)
    },
    onError: (err) => { setErrorMsg(err.message || 'Failed to save'); setSuccessMsg('') },
  })

  const addBand = () => {
    const lastMax = bands.length ? parseFloat(bands[bands.length - 1].maxMiles) || 0 : 0
    setBands(prev => [...prev, { maxMiles: String(lastMax + 2), fee: '0' }])
  }
  const removeBand = (i) => setBands(prev => prev.filter((_, idx) => idx !== i))
  const updateBand = (i, key, val) => setBands(prev => prev.map((b, idx) => (idx === i ? { ...b, [key]: val } : b)))

  const handleSave = () => {
    const parsed = bands.map(b => ({ maxMiles: parseFloat(b.maxMiles), fee: parseFloat(b.fee) }))
    if (parsed.length === 0) { setErrorMsg('Add at least one fee tier.'); return }
    for (const [i, b] of parsed.entries()) {
      if (isNaN(b.maxMiles) || b.maxMiles <= 0) { setErrorMsg(`Tier ${i + 1}: enter a valid distance in miles.`); return }
      if (isNaN(b.fee) || b.fee < 0) { setErrorMsg(`Tier ${i + 1}: enter a valid fee.`); return }
    }
    const maxSet = new Set(parsed.map(b => b.maxMiles))
    if (maxSet.size !== parsed.length) { setErrorMsg('Each tier needs a distinct "up to" distance.'); return }
    setErrorMsg('')
    const sorted = [...parsed].sort((a, b) => a.maxMiles - b.maxMiles)
    mutSave({
      variables: {
        mode: 'DISTANCE',
        bands: sorted.map((b, idx) => ({ maxMiles: b.maxMiles, fee: b.fee, sortOrder: idx })),
      },
    })
  }

  const sortedForLabels = [...bands].sort((a, b) => (parseFloat(a.maxMiles) || 0) - (parseFloat(b.maxMiles) || 0))
  const lowerBoundFor = (maxMiles) => {
    const m = parseFloat(maxMiles)
    let lower = 0
    for (const b of sortedForLabels) {
      const bm = parseFloat(b.maxMiles)
      if (bm < m) lower = bm
    }
    return lower
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <RouteOutlinedIcon sx={{ color: '#4f46e5' }} />
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>Delivery fee — by driving distance</Typography>
          <Typography variant="body2" color="text.secondary">
            Charged on every platform order, based on the actual road distance from the restaurant.
          </Typography>
        </Box>
      </Box>

      {successMsg && <Alert severity="success" sx={{ my: 2 }}>{successMsg}</Alert>}
      {errorMsg && <Alert severity="error" sx={{ my: 2 }}>{errorMsg}</Alert>}

      <Box sx={{ mt: 2.5 }}>
        {bands.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No tiers yet. Add one to start (e.g. up to 2 mi → $2.99).
          </Typography>
        )}

        {bands.map((b, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ width: 56, flexShrink: 0, textAlign: 'right', color: 'text.secondary', fontWeight: 600, fontSize: 13 }}>
              {lowerBoundFor(b.maxMiles)} –
            </Box>
            <TextField
              label="Up to" value={b.maxMiles}
              onChange={e => updateBand(i, 'maxMiles', e.target.value)}
              size="small" type="number" inputProps={{ min: 0.1, step: 0.5 }}
              InputProps={{ endAdornment: <InputAdornment position="end">mi</InputAdornment> }}
              sx={{ width: 140 }}
            />
            <TextField
              label="Fee" value={b.fee}
              onChange={e => updateBand(i, 'fee', e.target.value)}
              size="small" type="number" inputProps={{ min: 0, step: 0.01 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ width: 130 }}
            />
            <Tooltip title="Remove tier">
              <IconButton size="small" onClick={() => removeBand(i)}
                sx={{ color: '#9e9e9e', '&:hover': { color: '#d32f2f' } }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}

        <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addBand}
          variant="outlined" sx={{ mt: 0.5, textTransform: 'none', borderRadius: 2 }}>
          Add tier
        </Button>
      </Box>

      <Alert severity="info" icon={false} sx={{ mt: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', fontSize: 13 }}>
        <strong>Good to know</strong>
        <Box component="ul" sx={{ pl: 2.5, m: '6px 0 0' }}>
          <li>The farthest tier’s price also applies <strong>past its limit</strong> — e.g. with only “up to 5 mi = $5.99”, a customer at 7 mi still pays $5.99.</li>
          <li>How far each restaurant delivers is set by its <strong>delivery boundary</strong> (Boundaries tab); customers outside it can’t order. Add more tiers if you want farther customers to pay more.</li>
        </Box>
      </Alert>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {saving ? 'Saving…' : 'Save fee tiers'}
        </Button>
      </Box>
    </Box>
  )
}
